import {useEffect, useRef, useCallback} from 'react';
import {auth} from '@src/firebase';
import {useVodacomRefreshMutation} from '@src/Api/TraceApi/traceApi';
import useIsLoggedIn from '@src/Providers/AuthProvider/useIsLoggedIn';
import {useAppSelector} from '@src/Store/hooks';

const REFRESH_INTERVAL_MS = 45 * 60 * 1000; // 45 minutes
const EXPIRY_BUFFER_MS = 2 * 60 * 60 * 1000; // 2 hours before expiry → trigger refresh

/**
 * Periodically re-verifies the Vodacom subscription against Penrose
 * via POST /auth/vodacom/refresh.
 *
 * The backend reads emsisdn/msisdn from Firebase custom claims — no
 * client-side token management needed.
 *
 * - Runs immediately on load if the subscription is expired or near expiry.
 * - Runs every 45 min while the user is logged in as a Vodacom user.
 * - On "refreshed": subscription extended by 24h server-side.
 * - On "logout" / "redirect_to_penrose": signs the user out.
 */
export function useVodacomSessionRefresh() {
  const [vodacomRefresh] = useVodacomRefreshMutation();
  const {isLoggedIn} = useIsLoggedIn();
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const hasRunInitialRef = useRef(false);

  const memberSubscription = useAppSelector(
    state => state.member.memberSubscription,
  );
  const isVodacom = memberSubscription?.carrier === 'vodacom';

  const doRefresh = useCallback(async () => {
    console.log('[Vodacom Refresh] Refreshing subscription…');

    try {
      const response = await vodacomRefresh().unwrap();

      switch (response.action) {
        case 'refreshed':
          console.log('[Vodacom Refresh] Subscription renewed');
          break;

        case 'logout':
          console.warn('[Vodacom Refresh] Logged out:', response.reason);
          await auth.signOut();
          break;

        case 'redirect_to_penrose':
          console.warn(
            '[Vodacom Refresh] Subscription inactive, redirecting',
          );
          await auth.signOut();
          if (response.url) {
            window.location.href = response.url;
          }
          break;
      }
    } catch (err) {
      console.warn('[Vodacom Refresh] Failed:', err);
    }
  }, [vodacomRefresh]);

  useEffect(() => {
    if (!isLoggedIn || !isVodacom) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      hasRunInitialRef.current = false;
      return;
    }

    // Immediate refresh if subscription is expired or near expiry
    if (!hasRunInitialRef.current) {
      hasRunInitialRef.current = true;
      const subEnd = memberSubscription?.subscriptionEnd;
      if (subEnd) {
        const expiresAt = new Date(subEnd).getTime();
        const now = Date.now();
        if (expiresAt - now < EXPIRY_BUFFER_MS) {
          doRefresh();
        }
      }
    }

    intervalRef.current = setInterval(doRefresh, REFRESH_INTERVAL_MS);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isLoggedIn, isVodacom, doRefresh, memberSubscription?.subscriptionEnd]);
}

export default useVodacomSessionRefresh;
